import { ContentCanvas, LessonSidebar } from '../../components';
import type {
  LessonBlockTranslationFields,
  LessonBlockTranslationRecord,
  TranslationReplacementAction,
} from '@course-authoring/shared-types';
import { lessonEditorPageStyles as styles } from './lessonEditorPageStyles';

interface LessonEditorEditProps {
  isLocked: boolean;
  lessonId?: string;
  /** NA-437: block to scroll-into-view and highlight after "Open that lesson after copying". */
  highlightBlockId?: string | null;
  /** NA-437: navigates to a different lesson's editor — see ContentCanvas.tsx for why this is injected rather than called directly. */
  onNavigateToLesson?: (lessonId: string, options?: { highlightBlockId?: string }) => void;
  /** `{ [blockId]: externalAccountId[] }`; undefined outside Standard/English context (NA-295). */
  blockCustomizations?: Record<string, number[]>;
  blockCustomizationsLoading?: boolean;
  blockCustomizationsError?: boolean;
  onRetryBlockCustomizations?: () => void;
  translation?: {
    language: string;
    records: LessonBlockTranslationRecord[];
    drafts: Record<string, LessonBlockTranslationFields>;
    onChange: (blockId: string, updates: LessonBlockTranslationFields) => void;
    onPreviewFromBlock: (blockId: string) => void;
  };
  englishTranslation?: {
    records: LessonBlockTranslationRecord[];
    decisions: Record<string, {
      action: TranslationReplacementAction;
      committed: boolean;
    } | undefined>;
    onDecision: (blockId: string, action: TranslationReplacementAction) => void;
    onSourceChange: (blockId: string) => void;
  };
}

export function LessonEditorEdit({
  isLocked,
  lessonId,
  highlightBlockId,
  onNavigateToLesson,
  blockCustomizations,
  blockCustomizationsLoading,
  blockCustomizationsError,
  onRetryBlockCustomizations,
  translation,
  englishTranslation,
}: LessonEditorEditProps) {
  // Published — locked: fields read-only, block controls inert, content dimmed
  // but readable (NA-207). Each view applies its own inertness (LessonSidebar
  // disabled, ContentCanvas publishLocked) rather than a pointer-events
  // blanket, so the panes still scroll and per-block Preview stays clickable.
  return (
    <div style={styles.mainLayout}>
      <LessonSidebar
        disabled={isLocked}
        translationRecords={translation?.records}
        lessonId={lessonId}
        blockCustomizations={blockCustomizations}
        blockCustomizationsLoading={blockCustomizationsLoading}
        blockCustomizationsError={blockCustomizationsError}
        onRetryBlockCustomizations={onRetryBlockCustomizations}
      />
      <ContentCanvas
        publishLocked={isLocked}
        translation={translation}
        englishTranslation={englishTranslation}
        highlightBlockId={highlightBlockId}
        onNavigateToLesson={onNavigateToLesson}
      />
    </div>
  );
}
